import React, { useEffect, useRef } from "react";
import { pickColor, rgba } from "./internal/colorUtils";

export const key = "hex-pulse";

const DEFAULT_BG = "#0a0f1f";
const DEFAULT_HEX = "#1e293b";
const DEFAULT_PULSE = "#22d3ee";

const HEX_RADIUS = 22;          // outer radius (px) of each hex
const PULSE_SPEED = 90;         // px per second the wavefront travels
const PULSE_THICKNESS = 110;    // px width of the lit ring
const EPI_SPEED = 0.3;          // how fast the pulse origin drifts
const PULSE_INTERVAL_MS = 1900; // ms between successive pulses
const MAX_PULSES = 4;           // concurrent pulses

function hexCorner(cx, cy, i, r) {
  // Pointy-top hex corners (start at top, go clockwise).
  const angle = (Math.PI / 3) * i - Math.PI / 2;
  return [cx + r * Math.cos(angle), cy + r * Math.sin(angle)];
}

export default function HexPulse({ colors = [], style }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const colorsRef = useRef([DEFAULT_BG, DEFAULT_HEX, DEFAULT_PULSE]);
  const stateRef = useRef({
    pulses: [],
    epicenter: { x: 0, y: 0, vx: EPI_SPEED, vy: EPI_SPEED * 0.7 },
    lastPulseAt: 0,
  });

  useEffect(() => {
    colorsRef.current = [
      pickColor(colors, 0, DEFAULT_BG),
      pickColor(colors, 1, DEFAULT_HEX),
      pickColor(colors, 2, DEFAULT_PULSE),
    ];
  }, [colors]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    let width = 0;
    let height = 0;
    const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;
    let lastTs = 0;

    // Pre-computed hex grid centres. Re-built on resize.
    let centers = [];

    const buildCenters = () => {
      centers = [];
      // Pointy-top hex layout. Horizontal spacing = sqrt(3)*r, vertical = 1.5*r.
      const dx = Math.sqrt(3) * HEX_RADIUS;
      const dy = 1.5 * HEX_RADIUS;
      for (let row = -1, y = -HEX_RADIUS; y < height + HEX_RADIUS; row += 1, y += dy) {
        const offset = row % 2 === 0 ? 0 : dx / 2;
        for (let x = -dx + offset; x < width + dx; x += dx) {
          centers.push({ x, y });
        }
      }
    };

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      // Use clientWidth/Height (layout dims) so an ancestor `transform: scale(...)`
      // (e.g. PreviewDeviceStage) doesn't shrink the canvas and pin it to top-left.
      width = Math.max(1, parent.clientWidth || parent.offsetWidth || 0);
      height = Math.max(1, parent.clientHeight || parent.offsetHeight || 0);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      buildCenters();
      const s = stateRef.current;
      s.epicenter.x = width / 2;
      s.epicenter.y = height / 2;
    };

    const drawHex = (cx, cy, fillStyle, strokeStyle) => {
      ctx.beginPath();
      const [x0, y0] = hexCorner(cx, cy, 0, HEX_RADIUS);
      ctx.moveTo(x0, y0);
      for (let i = 1; i < 6; i += 1) {
        const [x, y] = hexCorner(cx, cy, i, HEX_RADIUS);
        ctx.lineTo(x, y);
      }
      ctx.closePath();
      if (fillStyle) {
        ctx.fillStyle = fillStyle;
        ctx.fill();
      }
      if (strokeStyle) {
        ctx.strokeStyle = strokeStyle;
        ctx.stroke();
      }
    };

    const tick = (ts) => {
      if (!lastTs) lastTs = ts;
      const dt = Math.min(50, ts - lastTs);
      lastTs = ts;

      const [bg, hexColor, pulseColor] = colorsRef.current;
      const s = stateRef.current;

      // Background.
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, width, height);

      // Drift epicenter; bounce off bounds gently.
      s.epicenter.x += s.epicenter.vx * (dt / 16);
      s.epicenter.y += s.epicenter.vy * (dt / 16);
      if (s.epicenter.x < 0 || s.epicenter.x > width) s.epicenter.vx *= -1;
      if (s.epicenter.y < 0 || s.epicenter.y > height) s.epicenter.vy *= -1;

      // Spawn a new pulse on cadence; cap concurrency.
      if (ts - s.lastPulseAt > PULSE_INTERVAL_MS && s.pulses.length < MAX_PULSES) {
        s.pulses.push({
          x: s.epicenter.x,
          y: s.epicenter.y,
          startTs: ts,
        });
        s.lastPulseAt = ts;
      }

      // Advance + cull pulses. A pulse dies once its wavefront has left
      // the canvas diagonally (longest possible travel distance).
      const maxDist = Math.sqrt(width * width + height * height);
      for (let i = s.pulses.length - 1; i >= 0; i -= 1) {
        const p = s.pulses[i];
        const age = (ts - p.startTs) / 1000;
        const radius = age * PULSE_SPEED;
        if (radius - PULSE_THICKNESS > maxDist) {
          s.pulses.splice(i, 1);
        }
      }

      ctx.lineWidth = 1;
      // Dim the whole grid as a base layer; per-hex pulse glow goes on top.
      for (let i = 0; i < centers.length; i += 1) {
        const c = centers[i];
        // Compute peak intensity contribution from any active pulse.
        let intensity = 0;
        for (let j = 0; j < s.pulses.length; j += 1) {
          const p = s.pulses[j];
          const age = (ts - p.startTs) / 1000;
          const radius = age * PULSE_SPEED;
          const dx = c.x - p.x;
          const dy = c.y - p.y;
          const d = Math.sqrt(dx * dx + dy * dy);
          const delta = Math.abs(d - radius);
          if (delta < PULSE_THICKNESS) {
            // Bell curve falloff inside the ring.
            const k = 1 - delta / PULSE_THICKNESS;
            const fade = Math.max(0, 1 - radius / maxDist);
            intensity = Math.max(intensity, k * k * fade);
          }
        }

        if (intensity > 0.02) {
          drawHex(c.x, c.y, rgba(pulseColor, 0.45 * intensity), rgba(pulseColor, 0.6 + 0.4 * intensity));
        } else {
          drawHex(c.x, c.y, null, rgba(hexColor, 0.65));
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    resize();
    rafRef.current = requestAnimationFrame(tick);

    let ro = null;
    if (typeof ResizeObserver !== "undefined" && canvas.parentElement) {
      ro = new ResizeObserver(resize);
      ro.observe(canvas.parentElement);
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", resize);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ro) {
        try { ro.disconnect(); } catch { /* noop */ }
      } else if (typeof window !== "undefined") {
        window.removeEventListener("resize", resize);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        pointerEvents: "none",
        ...style,
      }}
    />
  );
}
